import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Grid,
  Chip,
  MenuItem,
  Select,
  InputLabel,
  Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import InfoIcon from '@mui/icons-material/Info';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { db } from '../../../../firebase';
import { collection, addDoc, writeBatch, Timestamp, doc } from 'firebase/firestore';

interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
}

const steps = ['Select Source', 'Map Fields', 'Preview & Import'];

const ImportDialog: React.FC<ImportDialogProps> = ({ open, onClose, organizationId }) => {
  const [activeStep, setActiveStep] = useState(0);
  const [importSource, setImportSource] = useState<string>('csv');
  const [file, setFile] = useState<File | null>(null);
  const [csvData, setCsvData] = useState<string[][]>([]);
  const [columnMappings, setColumnMappings] = useState<{[key: string]: number}>({});
  const [previewData, setPreviewData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [importResult, setImportResult] = useState<{success: number; failed: number; total: number} | null>(null);
  const [duplicateHandling, setDuplicateHandling] = useState<string>('skip');
  const [tagForImport, setTagForImport] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Required fields for contact import
  const requiredFields = ['firstName', 'email'];
  
  // All available fields for mapping
  const availableFields = [
    { id: 'firstName', label: 'First Name', required: true },
    { id: 'lastName', label: 'Last Name', required: false },
    { id: 'email', label: 'Email', required: true },
    { id: 'phone', label: 'Phone Number', required: false },
    { id: 'company', label: 'Company', required: false },
    { id: 'title', label: 'Job Title', required: false },
    { id: 'notes', label: 'Notes', required: false },
    { id: 'tags', label: 'Tags (comma separated)', required: false },
  ];
  
  // Handle file selection
  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);
      
      // Parse CSV
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        parseCSV(text);
      };
      reader.readAsText(selectedFile);
    }
  };
  
  // Parse CSV function
  const parseCSV = (csvText: string) => {
    try {
      // Simple CSV parsing (you might want to use a library like papaparse in production)
      const lines = csvText.split('\\n');
      const result: string[][] = [];
      
      lines.forEach(line => {
        // Handle quoted values with commas inside
        const row: string[] = [];
        let inQuote = false;
        let currentValue = '';
        
        for (let i = 0; i < line.length; i++) {
          const char = line[i];
          
          if (char === '"' && (i === 0 || line[i-1] !== '\\\\')) {
            inQuote = !inQuote;
          } else if (char === ',' && !inQuote) {
            row.push(currentValue.trim());
            currentValue = '';
          } else {
            currentValue += char;
          }
        }
        
        // Add the last value
        row.push(currentValue.trim());
        result.push(row);
      });
      
      setCsvData(result);
      
      // Auto-detect column mappings
      if (result.length > 0) {
        const headers = result[0];
        const newMappings: {[key: string]: number} = {};
        
        // Try to match headers to fields
        headers.forEach((header, index) => {
          const lowerHeader = header.toLowerCase();
          
          if (lowerHeader.includes('first') || lowerHeader.includes('given')) {
            newMappings.firstName = index;
          } else if (lowerHeader.includes('last') || lowerHeader.includes('surname')) {
            newMappings.lastName = index;
          } else if (lowerHeader.includes('email')) {
            newMappings.email = index;
          } else if (lowerHeader.includes('phone')) {
            newMappings.phone = index;
          } else if (lowerHeader.includes('company') || lowerHeader.includes('organization')) {
            newMappings.company = index;
          } else if (lowerHeader.includes('title') || lowerHeader.includes('position')) {
            newMappings.title = index;
          } else if (lowerHeader.includes('note')) {
            newMappings.notes = index;
          } else if (lowerHeader.includes('tag')) {
            newMappings.tags = index;
          }
        });
        
        setColumnMappings(newMappings);
      }
    } catch (error) {
      console.error('Error parsing CSV:', error);
      setErrorMessage('Failed to parse CSV file. Please check the format and try again.');
    }
  };
  
  // Handle column mapping change
  const handleMappingChange = (field: string, columnIndex: number) => {
    setColumnMappings({
      ...columnMappings,
      [field]: columnIndex
    });
  };
  
  // Handle next step
  const handleNext = () => {
    if (activeStep === 0) {
      // Validate file is selected
      if (!file) {
        setErrorMessage('Please select a file to import');
        return;
      }
      setErrorMessage(null);
    } else if (activeStep === 1) {
      // Validate required mappings
      const missingRequiredFields = requiredFields.filter(field => 
        columnMappings[field] === undefined
      );
      
      if (missingRequiredFields.length > 0) {
        setErrorMessage(`Please map required fields: ${missingRequiredFields.join(', ')}`);
        return;
      }
      
      // Generate preview data
      generatePreviewData();
      setErrorMessage(null);
    } else if (activeStep === 2) {
      // Start import process
      importContacts();
    }
    
    setActiveStep((prevStep) => prevStep + 1);
  };
  
  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
    setErrorMessage(null);
  };
  
  // Generate preview data
  const generatePreviewData = () => {
    if (csvData.length <= 1) {
      setPreviewData([]);
      return;
    }
    
    // Skip header row
    const dataRows = csvData.slice(1);
    
    // Generate preview data for up to 5 rows
    const preview = dataRows.slice(0, 5).map(row => {
      const contact: {[key: string]: any} = {};
      
      // Map fields based on column mappings
      Object.entries(columnMappings).forEach(([field, columnIndex]) => {
        if (columnIndex !== undefined && columnIndex < row.length) {
          contact[field] = row[columnIndex];
        }
      });
      
      // Format tags if present
      if (contact.tags) {
        contact.tags = contact.tags.split(',').map((tag: string) => tag.trim());
      } else {
        contact.tags = [];
      }
      
      // Add import tag if specified
      if (tagForImport) {
        contact.tags = [...(contact.tags || []), tagForImport];
      }
      
      return contact;
    });
    
    setPreviewData(preview);
  };
  
  // Import contacts
  const importContacts = async () => {
    if (csvData.length <= 1) return;
    
    setLoading(true);
    
    try {
      // Skip header row
      const dataRows = csvData.slice(1);
      let successCount = 0;
      let failedCount = 0;
      
      // Use batched writes for better performance
      const batch = writeBatch(db);
      const contactsRef = collection(db, `organizations/${organizationId}/contacts`);
      
      for (const row of dataRows) {
        try {
          const contact: {[key: string]: any} = {
            createdAt: Timestamp.now(),
            source: 'csv_import',
            consentStatus: 'unknown',
            engagementScore: 0,
            importDate: new Date().toISOString(), // Save import date
          };
          
          // Map fields based on column mappings
          Object.entries(columnMappings).forEach(([field, columnIndex]) => {
            if (columnIndex !== undefined && columnIndex < row.length) {
              contact[field] = row[columnIndex];
            }
          });
          
          // Format tags
          if (contact.tags) {
            contact.tags = contact.tags.split(',').map((tag: string) => tag.trim());
          } else {
            contact.tags = [];
          }
          
          // Add import tag if specified
          if (tagForImport) {
            contact.tags = [...(contact.tags || []), tagForImport];
          }
          
          // Validate required fields
          const missingRequiredFields = requiredFields.filter(field => 
            !contact[field]
          );
          
          if (missingRequiredFields.length > 0) {
            failedCount++;
            continue;
          }
          
          // Add to batch
          const newContactRef = doc(contactsRef);
          batch.set(newContactRef, contact);
          successCount++;
        } catch (error) {
          failedCount++;
        }
      }
      
      // Commit the batch
      await batch.commit();
      
      setImportResult({
        success: successCount,
        failed: failedCount,
        total: dataRows.length
      });
    } catch (error) {
      console.error('Error importing contacts:', error);
      setErrorMessage('Failed to import contacts. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  // Reset state when dialog closes
  const handleDialogClose = () => {
    onClose();
    setActiveStep(0);
    setImportSource('csv');
    setFile(null);
    setCsvData([]);
    setColumnMappings({});
    setPreviewData([]);
    setLoading(false);
    setImportResult(null);
    setDuplicateHandling('skip');
    setTagForImport('');
    setErrorMessage(null);
  };
  
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : handleDialogClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="import-dialog-title"
    >
      <DialogTitle id="import-dialog-title">
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Import Contacts</Typography>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleDialogClose}
            disabled={loading}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <Divider />
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {errorMessage}
          </Alert>
        )}
        
        {activeStep === 0 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Choose import source
            </Typography>
            
            <FormControl component="fieldset" sx={{ mb: 3 }}>
              <RadioGroup
                aria-label="import-source"
                name="import-source"
                value={importSource}
                onChange={(e) => setImportSource(e.target.value)}
              >
                <FormControlLabel value="csv" control={<Radio />} label="CSV or Excel file" />
                <FormControlLabel value="eventbrite" control={<Radio />} label="Eventbrite (coming soon)" disabled />
                <FormControlLabel value="meetup" control={<Radio />} label="Meetup (coming soon)" disabled />
                <FormControlLabel value="mailchimp" control={<Radio />} label="Mailchimp (coming soon)" disabled />
              </RadioGroup>
            </FormControl>
            
            {importSource === 'csv' && (
              <Box>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 3,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    cursor: 'pointer',
                    borderStyle: 'dashed',
                    borderColor: file ? 'primary.main' : 'divider',
                    backgroundColor: file ? 'primary.50' : 'background.paper',
                    '&:hover': {
                      borderColor: 'primary.main',
                      backgroundColor: 'primary.50'
                    }
                  }}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    type="file"
                    ref={fileInputRef}
                    accept=".csv,.xlsx,.xls"
                    style={{ display: 'none' }}
                    onChange={handleFileChange}
                  />
                  
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    {file ? (
                      <>
                        <CheckCircleIcon color="primary" sx={{ fontSize: 48, mb: 2 }} />
                        <Typography variant="h6">{file.name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {(file.size / 1024).toFixed(1)} KB • {csvData.length > 0 ? csvData.length - 1 : 0} contacts
                        </Typography>
                        <Button
                          variant="outlined"
                          size="small"
                          sx={{ mt: 2 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setFile(null);
                            setCsvData([]);
                            if (fileInputRef.current) fileInputRef.current.value = '';
                          }}
                        >
                          Change File
                        </Button>
                      </>
                    ) : (
                      <>
                        <CloudUploadIcon sx={{ fontSize: 48, mb: 2, color: 'text.secondary' }} />
                        <Typography variant="h6">Upload CSV or Excel file</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                          Drag and drop or click to browse
                        </Typography>
                      </>
                    )}
                  </Box>
                </Paper>
                
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tag all imported contacts (optional)
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    label="Tag"
                    value={tagForImport}
                    onChange={(e) => setTagForImport(e.target.value)}
                    placeholder="e.g., April 2025 Import"
                    helperText="This tag will be added to all imported contacts"
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Map CSV columns to contact fields
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Please match your CSV columns to the corresponding contact fields. Required fields are marked with an asterisk (*).
            </Typography>
            
            {csvData.length > 0 && (
              <Box>
                <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    CSV Header Preview
                  </Typography>
                  <Grid container spacing={1}>
                    {csvData[0].map((header, index) => (
                      <Grid item key={index}>
                        <Chip 
                          label={`${index + 1}: ${header}`} 
                          variant="outlined" 
                          size="small"
                        />
                      </Grid>
                    ))}
                  </Grid>
                </Paper>
                
                <List>
                  {availableFields.map((field) => (
                    <ListItem key={field.id} divider>
                      <ListItemText 
                        primary={
                          <Box component="span">
                            {field.label}
                            {field.required && (
                              <Typography component="span" color="error">*</Typography>
                            )}
                          </Box>
                        }
                      />
                      <FormControl sx={{ width: 200 }}>
                        <InputLabel id={`map-${field.id}-label`}>Map to Column</InputLabel>
                        <Select
                          labelId={`map-${field.id}-label`}
                          value={columnMappings[field.id] !== undefined ? columnMappings[field.id] : ''}
                          onChange={(e) => handleMappingChange(field.id, e.target.value as number)}
                          label="Map to Column"
                          size="small"
                        >
                          <MenuItem value="">
                            <em>Do not import</em>
                          </MenuItem>
                          {csvData[0].map((header, index) => (
                            <MenuItem key={index} value={index}>
                              {`${index + 1}: ${header}`}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </ListItem>
                  ))}
                </List>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Duplicate Handling
                  </Typography>
                  <FormControl component="fieldset">
                    <RadioGroup
                      aria-label="duplicate-handling"
                      name="duplicate-handling"
                      value={duplicateHandling}
                      onChange={(e) => setDuplicateHandling(e.target.value)}
                    >
                      <FormControlLabel value="skip" control={<Radio />} label="Skip duplicates" />
                      <FormControlLabel value="update" control={<Radio />} label="Update existing contacts" />
                      <FormControlLabel value="keep" control={<Radio />} label="Keep duplicates (create new contacts)" />
                    </RadioGroup>
                  </FormControl>
                </Box>
              </Box>
            )}
          </Box>
        )}
        
        {activeStep === 2 && !importResult && (
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Preview Import
            </Typography>
            
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Review the data before importing. The preview shows up to 5 contacts from your file.
            </Typography>
            
            {previewData.length > 0 ? (
              <Paper variant="outlined" sx={{ mb: 3 }}>
                <List>
                  {previewData.map((contact, index) => (
                    <ListItem key={index} divider>
                      <Box sx={{ width: '100%' }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                          <Typography variant="subtitle2">
                            {contact.firstName} {contact.lastName || ''}
                          </Typography>
                          <Chip
                            label="Preview"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                        
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Email: {contact.email || 'N/A'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Phone: {contact.phone || 'N/A'}
                            </Typography>
                          </Grid>
                          {contact.company && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Company: {contact.company}
                              </Typography>
                            </Grid>
                          )}
                          {contact.title && (
                            <Grid item xs={12} sm={6}>
                              <Typography variant="body2" color="text.secondary">
                                Title: {contact.title}
                              </Typography>
                            </Grid>
                          )}
                          {contact.tags && contact.tags.length > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                {contact.tags.map((tag: string, tagIndex: number) => (
                                  <Chip 
                                    key={tagIndex} 
                                    label={tag} 
                                    size="small" 
                                    variant="outlined"
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}
                        </Grid>
                      </Box>
                    </ListItem>
                  ))}
                </List>
              </Paper>
            ) : (
              <Alert severity="warning" sx={{ mb: 3 }}>
                No preview data available. Please go back and check your file and mappings.
              </Alert>
            )}
            
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                You are about to import {csvData.length > 1 ? csvData.length - 1 : 0} contacts. This action cannot be undone.
              </Typography>
            </Alert>
          </Box>
        )}
        
        {activeStep === 3 || importResult ? (
          <Box sx={{ textAlign: 'center', py: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <CircularProgress size={60} sx={{ mb: 2 }} />
                <Typography variant="h6">Importing Contacts...</Typography>
                <Typography variant="body2" color="text.secondary">
                  This may take a few moments
                </Typography>
              </Box>
            ) : importResult ? (
              <Box>
                <CheckCircleIcon color="success" sx={{ fontSize: 60, mb: 2 }} />
                <Typography variant="h5" gutterBottom>
                  Import Complete!
                </Typography>
                <Typography variant="body1" paragraph>
                  Successfully imported {importResult.success} contacts
                </Typography>
                
                {importResult.failed > 0 && (
                  <Alert severity="warning" sx={{ mb: 2 }}>
                    {importResult.failed} contacts could not be imported due to errors
                  </Alert>
                )}
                
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleDialogClose}
                  sx={{ mt: 2 }}
                >
                  Done
                </Button>
              </Box>
            ) : null}
          </Box>
        ) : null}
      </DialogContent>
      
      {activeStep < 3 && !importResult && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleBack}
            disabled={activeStep === 0 || loading}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={loading}
          >
            {activeStep === steps.length - 1 ? 'Import' : 'Next'}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default ImportDialog;
